import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, useFieldArray, FormProvider, Controller } from "react-hook-form";
import { Autocomplete, TextField } from "@mui/material";
import WSTextField, { CurrencyType, TextType } from "../../Components/TextBox/WSTextBox";
import WSPhone from "../../Components/Phone/WSPhone";
import WSAutocompleteField from "../../Components/AutoComplete/AutoComplete";
import WSDateField from "../../Components/DateField/WSDateField";
import WSSelectField from "../../Components/Select/WSSelect";
import { WSIcons } from "../../commonFunctions/iIcons";
import WSButton from "../../Components/Button/WSButton";
import WSSCheckBoxField from "../../Components/CheckBox/WSCheckBox";
import WSFileUpload from "../../Components/FileUpload/WSFileUpload";
import { callAPI, Method, dateFormat } from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { useDispatch } from "react-redux";
import config from "../../Config/clientConfig.json";
import AddIcon from "@mui/icons-material/Add";

export const Purchase: React.FC<any> = React.memo((props) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const { showMessage } = useSnackbar();
  const dispatch = useDispatch();
  const imageRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [loadingSaveAndNew, setLoadingSaveAndNew] = useState(false);
  const [isRoundOffChecked, setIsRoundOffChecked] = useState(false);
  const [showExtraFields, setShowExtraFields] = useState(false);
  const [parties, setParties] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [partyLoading, setPartyLoading] = useState(true);
  const [productLoading, setProductLoading] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);

  const genId = () => Date.now().toString(36) + Math.random().toString(36).slice(2);
  const [paymentRows, setPaymentRows] = useState(() => [
    { id: genId(), paymentType: "", amount: "" },
  ]);

  const methods = useForm<any>({
    defaultValues: {
      party: null,
      phoneNumber: "",
      billNo: "",
      billDate: dateFormat(new Date(), "DD-MM-YYYY"),
      roundOff: "0",
      items: [{
        productId: "",
        itemCode: "",
        hsnCode: "",
        description: "",
        quantity: "",
        unit: "",
        price: "",
        discount: "0",
        tax: "0"
      }],
      paymentRows: [{ paymentType: "", amount: "" }],
      uploadBill: null,
    },
  });

  const { control, handleSubmit, watch, setValue, getValues, formState: { errors }, reset } = methods;
  const { fields, append, remove } = useFieldArray({
    name: "items",
    control,
  });

  const loadPurchaseData = async (purchaseId: string) => {
    try {
      setPurchaseLoading(true);
      const response = await callAPI(`Vyapar/Purchase/${purchaseId}`, Method.GET);
      const purchase = Array.isArray(response.data || response) ? response.data[0] || response[0] : response.data || response;

      reset({
        party: purchase.partyId || null,
        phoneNumber: purchase.phoneNumber || "",
        billNo: purchase.billNo || "",
        billDate: purchase.billDate ? dateFormat(new Date(purchase.billDate), "DD-MM-YYYY") : dateFormat(new Date(), "DD-MM-YYYY"),
        roundOff: purchase.roundOff || "0",
        round: purchase.roundOff?.toString() || "0",     // ✅ ADDED: round field
        total: purchase.total?.toString() || "0",
        items: purchase.items?.map((item: any) => ({
          productId: item.productId || "",
          categoryId: item.categoryId || "",
          itemCode: item.itemCode || "",
          hsnCode: item.hsnCode || "",
          description: item.description || "",
          quantity: item.quantity || 0,
          unit: item.unit || "",
          price: item.price?.toString() || "",
          discount: item.discount || 0,
          tax: item.tax || 0,
          amount: item.amount?.toString() || ""
        })) || [{
          productId: "", itemCode: "", hsnCode: "", description: "",
          quantity: "", unit: "1", price: "", discount: "0", tax: "0", amount: ""
        }],
        paymentRows: purchase.payments?.map((payment: any) => ({
          paymentType: payment.paymentType || "",
          amount: payment.amount?.toString() || ""
        })) || [{ paymentType: "", amount: "" }],
        uploadBill: null,
      });

      setPaymentRows(purchase.payments?.map((payment: any, index: number) => ({
        id: genId(),
        paymentType: payment.paymentType || "",
        amount: payment.amount?.toString() || ""
      })) || [{ id: genId(), paymentType: "", amount: "" }]);

      showMessage("Purchase data loaded successfully", "success");
    } catch (error) {
      console.error("Failed to load purchase:", error);
      showMessage("Failed to load purchase data", "error");
    } finally {
      setPurchaseLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setPartyLoading(true);
        const partyResponse = await callAPI("Vyapar/Party", Method.GET);
        setParties(Array.isArray(partyResponse.data || partyResponse) ? partyResponse.data || partyResponse : []);

        setProductLoading(true);
        const productResponse = await callAPI("Vyapar/Product", Method.GET);
        setProducts(Array.isArray(productResponse) ? productResponse : []);

        if (isEditMode && id) {
          await loadPurchaseData(id);
        }
      } catch (error) {
        console.error("Failed to load data:", error);
        showMessage("Failed to load required data", "error");
      } finally {
        setPartyLoading(false);
        setProductLoading(false);
      }
    };
    loadData();
  }, [showMessage, isEditMode, id]);

  const productOptions = products.map((product: any) => ({
    id: product.id,
    label: `${product.name} (${product.itemCode})`,
    value: product.id,
    itemCode: product.itemCode,
    hsnSac: product.hsnSac,
    name: product.name,
    unit: product.unit || "NOS",
    purchasePrice: product.purchasePrice || 0,
    gstPercent: product.gstPercent || 0,
    categoryId: product.categoryId || product.category?.id || ""
  }));

  const partyOptions = parties.map((party: any) => ({
    id: party.id,
    label: party.partyName || party.name,
    value: party.id
  }));

  const handleProductSelect = useCallback((selectedProduct: any, index: number) => {
    if (selectedProduct?.id) {
      setValue(`items.${index}.productId`, selectedProduct.id);
      setValue(`items.${index}.categoryId`, selectedProduct.categoryId || "");
      setValue(`items.${index}.itemCode`, selectedProduct.itemCode || '');
      setValue(`items.${index}.hsnCode`, selectedProduct.hsnSac || '');
      setValue(`items.${index}.description`, selectedProduct.name || '');
      setValue(`items.${index}.unit`, selectedProduct.unit || "");
      setValue(`items.${index}.price`, selectedProduct.purchasePrice?.toString() || '0');
      setValue(`items.${index}.tax`, selectedProduct.gstPercent?.toString() || '0');
    }
  }, [setValue]);

  const handleAddRow = () =>
    setPaymentRows((prev) => [...prev, { id: genId(), paymentType: "", amount: "" }]);

  const handleRemoveRow = (idToRemove: any) =>
    setPaymentRows((prev) => prev.filter((r) => r.id !== idToRemove));

  const onSubmit = async (data: any) => {
    try {
      const partyId = data.party;
      const formData = new FormData();

      formData.append("PartyId", partyId);
      formData.append("PhoneNumber", data.phoneNumber || "");
      formData.append("BillNo", data.billNo || "");
      formData.append("BillDate", dateFormat(new Date(data.billDate), "YYYY-MM-DD"));
      formData.append("RoundOff", "0");

      const items = getValues("items") || [];
      items.forEach((item: any, index: number) => {
        formData.append(`Items[${index}].ProductId`, item.productId || "");
        formData.append(`Items[${index}].CategoryId`, item.categoryId || "");
        formData.append(`Items[${index}].ItemCode`, item.itemCode || "");
        formData.append(`Items[${index}].HSNCode`, item.hsnCode || "");
        formData.append(`Items[${index}].Description`, item.description || "");
        formData.append(`Items[${index}].Quantity`, parseFloat(item.quantity || "0").toString());
        formData.append(`Items[${index}].Unit`, item.unit || "");
        formData.append(`Items[${index}].Price`, parseFloat(item.price || "0").toString());
        formData.append(`Items[${index}].Discount`, parseFloat(item.discount || "0").toString());
        formData.append(`Items[${index}].Tax`, parseFloat(item.tax || "0").toString());
      });
      const paymentRowsData = getValues("paymentRows") || [];
      paymentRowsData.forEach((payment: any, index: number) => {
        formData.append(`Payments[${index}].PaymentType`, payment.paymentType || "");
        formData.append(`Payments[${index}].Amount`, parseFloat(payment.amount || "0").toString());
      });

      let imageFile: File | null = null;
      if (imageRef.current?.files?.[0]) {
        imageFile = imageRef.current.files[0];
      }
      if (!imageFile) {
        const formImage = getValues('uploadBill') as FileList | File | null;
        if (formImage && (formImage as FileList)[0]) {
          imageFile = (formImage as FileList)[0];
        }
      }
      if (!imageFile) {
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput?.files?.[0]) {
          imageFile = fileInput.files[0];
        }
      }
      if (imageFile) {
        formData.append("BillFilePath", imageFile);
      }

      const apiEndpoint = isEditMode && id ? `Vyapar/Purchase/${id}` : "Vyapar/Purchase";
      const method = isEditMode && id ? Method.PUT : Method.POST;

      setLoading(true);
      await callAPI(apiEndpoint, method, formData);

      const message = isEditMode ? "Purchase updated successfully!" : "Purchase + Bill uploaded successfully!";
      showMessage(`✅ ${message}`, "success");
      navigate("/purchase/list");

    } catch (error: any) {
      console.error("❌ ERROR:", error.response?.data);
      showMessage("Failed to save. Check console.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDataSaveAndNew = () => { };

  // if (partyLoading || productLoading || (isEditMode && purchaseLoading)) {
  //   return <div>Loading required data...</div>;
  // }

  return (
    <FormProvider {...methods}>
      <Grid p={3}>
        <Paper
          elevation={0}
          style={{
            paddingLeft: 20,
            paddingRight: 20,
            marginTop: 0,
            paddingBottom: 25,
            borderRadius: "8px",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={11}>
              <Typography sx={{ fontSize: "1rem", fontWeight: "bold" }}>
                {isEditMode ? "Edit Purchase" : "Purchase"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={1}>
              <IconButton onClick={() => navigate("/")}>
                <WSIcons.CloseIcon />
              </IconButton>
            </Grid>

            <Grid item xs={12} sm={6}>
              <WSAutocompleteField
                rules={{ required: "Party is required" }}
                id="party-field"
                control={control}
                label="Vendor *"
                name="party"
                options={partyOptions}
              />
            </Grid>

            <Grid item xs="auto" display="flex" alignItems="center">
  <Box
    onClick={() => navigate("/purchase/add-party", { state: { from: "purchase" } })}
    sx={{
      height: "38px",
      width: "38px",
      borderRadius: "12px",
      background: "#f3f3f3",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      "&:hover": {
        background: "#e0e0e0",
      },
    }}
  >
    <AddIcon fontSize="small" />
  </Box>
</Grid>

            <Grid item xs={12} sm={6}>
              <WSPhone
                label="Phone Number"
                id="phone-field"
                name="phoneNumber"
                control={control}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <WSTextField
                label="Bill No *"
                id="billno-field"
                name="billNo"
                control={control}
                rules={{ required: "Bill No is required" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <WSDateField
                id="billdate-field"
                name="billDate"
                label="Bill Date *"
                dateFormat="DD-MM-YYYY"
                control={control}
                rules={{ required: "Bill Date is required" }}
              />
            </Grid>

            {fields.map((row, index) => (
              <Grid container spacing={2} key={row.id} alignItems="center" mt={1} ml={1}>
                <Grid item xs={12} sm={2}>
                  <Controller
                    name={`items.${index}.productId`}
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Autocomplete
                        options={productOptions}
                        value={productOptions.find(p => p.id === value) || null}
                        onChange={(_, selected) => {
                          onChange(selected?.id || "");
                          handleProductSelect(selected, index);
                        }}
                        getOptionLabel={(option) => option.label || ''}
                        isOptionEqualToValue={(option, val) => option.id === val}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Item *"
                            error={!!error}
                            helperText={error?.message}
                            size="small"
                          />
                        )}
                      />
                    )}
                  />
                </Grid>

               <Grid item xs="auto" display="flex" alignItems="center">
  <Box
    onClick={() => navigate("/purchase/product", { state: { from: "purchase" } })}
    sx={{
      height: "38px",
      width: "38px",
      borderRadius: "12px",
      background: "#f3f3f3",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      "&:hover": {
        background: "#e0e0e0",
      },
    }}
  >
    <AddIcon fontSize="small" />
  </Box>
</Grid>

                <Grid item xs={12} sm={2}>
                  <WSTextField
                    id={`itemcode-${index}`}
                    label="Item Code"
                    name={`items.${index}.itemCode`}
                    control={control}
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <WSTextField
                    id={`hsncode-${index}`}
                    label="HSN Code"
                    name={`items.${index}.hsnCode`}
                    control={control}
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <WSTextField
                    id={`description-${index}`}
                    label="Description"
                    name={`items.${index}.description`}
                    control={control}
                  />
                </Grid>

                <Grid item xs={12} sm={1}>
                  <WSTextField
                    id={`qty-${index}`}
                    label="QTY *"
                    name={`items.${index}.quantity`}
                    control={control}
                    rules={{ required: true, min: 0.01 }}
                    type={TextType.Number}
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <WSSelectField
                    label="Unit *"
                    id={`unit-${index}`}
                    name={`items.${index}.unit`}
                    control={control}
                    rules={{ required: true }}
                    options={config.unitTypeconfig.staticOptionData}
                  />
                </Grid>

                <Grid item xs={12} sm={1}>
                  <WSTextField
                    id={`price-${index}`}
                    label="Price/Unit *"
                    name={`items.${index}.price`}
                    control={control}
                    rules={{ required: true, min: 0 }}
                    type={TextType.Currency}
                    currency={CurrencyType.Rupee}
                  />
                </Grid>

                <Grid item xs={12} sm={1}>
                  <WSTextField
                    id={`discount-${index}`}
                    label="Discount"
                    name={`items.${index}.discount`}
                    control={control}
                    type={TextType.Number}
                  />
                </Grid>

                <Grid item xs={12} sm={1}>
                  <WSTextField
                    id={`tax-${index}`}
                    label="Tax"
                    name={`items.${index}.tax`}
                    control={control}
                    type={TextType.Number}
                  />
                </Grid>

                <Grid item xs={12} sm={1}>
                  <WSTextField
                    id={`amount-${index}`}
                    label="Amount"
                    name={`items.${index}.amount`}
                    control={control}
                    type={TextType.Currency}
                    currency={CurrencyType.Rupee}
                  />
                </Grid>

                <Grid item display="flex" justifyContent="center">
                  <WSIcons.DeleteIcon
                    onClick={() => remove(index)}
                    sx={{ color: "red", cursor: "pointer" }}
                  />
                </Grid>
              </Grid>
            ))}

            <Grid item xs={12} sx={{ marginLeft: 1 }}>
              <WSButton
                id="addButton"
                type="button"
                label="+ Add"
                size="small"
                onClick={() =>
                  append({
                    productId: "",
                    itemCode: "", hsnCode: "", description: "",
                    quantity: "", unit: "1", price: "", discount: "0", tax: "0", amount: ""
                  })
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              {!showExtraFields ? (
                <>
                  <WSSelectField
                    label="Payment Type"
                    id="paymentType"
                    name="paymentType"
                    control={control}
                    options={config.paymentType.staticOptionData}
                  />
                  <Button
                    variant="text"
                    size="small"
                    sx={{ textTransform: "none", p: 0, mt: 1 }}
                    onClick={() => setShowExtraFields(true)}
                  >
                    + Add Payment Details
                  </Button>
                </>
              ) : (
                <Paper sx={{ p: 2 }}>
                  {paymentRows.map((row, index) => (
                    <Grid container spacing={2} key={row.id} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={4}>
                        <WSSelectField
                          label="Payment Type"
                          id={`payment-type-${row.id}`}
                          name={`paymentRows.${index}.paymentType`}
                          control={control}
                          options={config.paymentType.staticOptionData}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <WSTextField
                          label="Amount"
                          id={`payment-amount-${row.id}`}
                          name={`paymentRows.${index}.amount`}
                          control={control}
                          type={TextType.Number}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <WSIcons.DeleteIcon
                          onClick={() => handleRemoveRow(row.id)}
                          sx={{ cursor: "pointer", color: "red" }}
                        />
                      </Grid>
                    </Grid>
                  ))}
                  <Button
                    variant="text"
                    size="small"
                    sx={{ textTransform: "none", p: 0, mt: 1 }}
                    onClick={handleAddRow}
                  >
                    + Add Payment Type
                  </Button>
                </Paper>
              )}
            </Grid>

            <Grid item xs={12} sm={2}>
              <WSSCheckBoxField
                id="roundoff-field"
                name="roundOff"
                label="Round Off"
                control={control}
                rules={{ required: false }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setIsRoundOffChecked(e.target.checked);
                  setValue("roundOff", e.target.checked ? "0" : "0");
                }}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <WSTextField
                id="round-field"
                name="round"
                label="Round"
                control={control}
                disabled={!isRoundOffChecked}
                type={TextType.Number}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <WSTextField
                id="total-field"
                name="total"
                label="Total"
                control={control}
                type={TextType.Number}
              />
            </Grid>

            <Grid item xs={12}>
              <WSFileUpload
                id="uploadBill"
                name="uploadBill"
                label="Upload Bill"
                control={control}
                rules={{ required: false }}
                acceptFileTypes=".jpg,.jpeg,.png"
                maxFileSizeMB={2}
                image={true}
                align="Start"
                isToolTipImg={false}
                multiple={true}
              />
            </Grid>
          </Grid>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container justifyContent="flex-end" spacing={2} sx={{ mt: 2 }}>
              <Grid item>
                <WSButton
                  id="save-new"
                  type="submit"
                  label="Save & New"
                  size="medium"
                  variant="outlined"
                  color="primary"
                  onClick={handleSubmit(handleDataSaveAndNew)}
                  loading={loadingSaveAndNew}

                />
              </Grid>
              <Grid item>
                <WSButton
                  id="save-purchase"
                  type="submit"
                  label="Save"
                  size="medium"
                  variant="contained"
                  color="primary"
                  loading={loading}

                />
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Grid>
    </FormProvider>
  );
});

export default Purchase;
